import { useState, useEffect } from 'react';
import { Navigation } from '../../components/Navigation';
import { FloatingBackButton } from '../../components/FloatingBackButton';
import { useAuthStore } from '../../store/authStore';
import { useCreditsStore } from '../../store/creditsStore';
import { supabase } from '../../lib/supabase';
import {
  Coins,
  Sparkles,
  TrendingUp,
  Star,
  Zap,
  FileText,
  Heart,
  Upload,
  Award,
  History,
  ArrowRight,
  Plus,
  Briefcase,
  Eye,
  Target,
  Check,
  ShoppingBag
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface CreditUse {
  name: string;
  cost: number;
  icon: any;
  description: string;
  color: string;
  link?: string;
  category: 'earn' | 'spend';
}

interface RecentTransaction {
  id: string;
  amount: number;
  type: string;
  description: string;
  created_at: string;
}

export function Discover() {
  const { user, profile } = useAuthStore();
  const { balance, fetchCredits } = useCreditsStore();
  const [recentTransactions, setRecentTransactions] = useState<RecentTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);

  useEffect(() => {
    if (user && profile) {
      fetchCredits(profile.id);
      loadRecentTransactions();
    }
  }, [user, profile]);

  const loadRecentTransactions = async () => {
    if (!profile) return;

    setLoadingTransactions(true);
    try {
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setRecentTransactions(data || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const earningOpportunities: CreditUse[] = [
    {
      name: 'Show Interest in Job',
      cost: 5,
      icon: Heart,
      description: 'Swipe right on any job posting',
      color: 'from-pink-500 to-red-500',
      link: '/swipe',
      category: 'earn',
    },
    {
      name: 'Complete Profile',
      cost: 10,
      icon: Award,
      description: 'Fill out all profile information',
      color: 'from-green-500 to-teal-500',
      link: '/jobseeker/settings',
      category: 'earn',
    },
    {
      name: 'Upload Resume',
      cost: 15,
      icon: Upload,
      description: 'Upload your first resume',
      color: 'from-blue-500 to-cyan-500',
      link: '/jobseeker/resumes',
      category: 'earn',
    },
    {
      name: 'Signup Bonus',
      cost: 100,
      icon: Star,
      description: 'Welcome bonus for new users',
      color: 'from-amber-500 to-orange-500',
      category: 'earn',
    },
    {
      name: 'Refer a Friend',
      cost: 25,
      icon: Coins,
      description: 'Earn when friends sign up with your code',
      color: 'from-orange-500 to-amber-500',
      link: '/jobseeker/referrals',
      category: 'earn',
    },
    {
      name: 'Friend Subscribes',
      cost: 75,
      icon: TrendingUp,
      description: 'Extra bonus when referred user subscribes',
      color: 'from-green-500 to-emerald-500',
      link: '/jobseeker/referrals',
      category: 'earn',
    },
  ];

  const spendingOptions: CreditUse[] = [
    {
      name: 'Build New Resume',
      cost: 10,
      icon: Sparkles,
      description: 'AI-generated professional resume from scratch',
      color: 'from-cyan-500 to-blue-500',
      link: '/jobseeker/resume-builder',
      category: 'spend',
    },
    {
      name: 'Refresh Resume',
      cost: 8,
      icon: TrendingUp,
      description: 'Completely rewrite and optimize existing resume',
      color: 'from-blue-500 to-cyan-600',
      link: '/jobseeker/resumes',
      category: 'spend',
    },
    {
      name: 'Generate Cover Letter',
      cost: 5,
      icon: FileText,
      description: 'AI-generated cover letter for any job',
      color: 'from-orange-500 to-amber-500',
      link: '/swipe',
      category: 'spend',
    },
    {
      name: 'Add Job to Resume',
      cost: 4,
      icon: Plus,
      description: 'Add new work experience with AI-enhanced bullets',
      color: 'from-amber-500 to-yellow-500',
      link: '/jobseeker/resumes',
      category: 'spend',
    },
    {
      name: 'Improve Bullet Points',
      cost: 2,
      icon: Target,
      description: 'Make your achievements more impactful',
      color: 'from-green-500 to-emerald-500',
      link: '/jobseeker/resumes',
      category: 'spend',
    },
    {
      name: 'Entry-Level Job Apply',
      cost: 2,
      icon: Zap,
      description: 'AI applies for you with customized resume',
      color: 'from-cyan-500 to-blue-400',
      link: '/swipe',
      category: 'spend',
    },
    {
      name: 'Mid-Level Job Apply',
      cost: 3,
      icon: Zap,
      description: 'AI applies with tailored resume and cover letter',
      color: 'from-blue-500 to-cyan-600',
      link: '/swipe',
      category: 'spend',
    },
    {
      name: 'High-Paying Job Apply',
      cost: 5,
      icon: Zap,
      description: 'Premium AI application for senior positions',
      color: 'from-orange-500 to-red-500',
      link: '/swipe',
      category: 'spend',
    },
    {
      name: 'Profile Visibility Boost',
      cost: 10,
      icon: Eye,
      description: 'Increase your profile views by 10x',
      color: 'from-purple-500 to-pink-500',
      link: '/profile-visibility-boost',
      category: 'spend',
    },
    {
      name: 'Interview Fast Pass',
      cost: 15,
      icon: Award,
      description: 'Get priority interview scheduling',
      color: 'from-red-500 to-pink-500',
      link: '/interview-fastpass',
      category: 'spend',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />
      <FloatingBackButton />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-3xl p-8 mb-12 text-white shadow-xl">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Briefcase className="w-10 h-10" />
                <h1 className="text-4xl font-bold">Discover</h1>
              </div>
              <p className="text-white/90 text-lg">
                Your hub for earning and spending credits
              </p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-8 py-4 rounded-2xl">
              <div className="text-sm text-white/80 mb-1">Your Balance</div>
              <div className="text-4xl font-bold">{balance}</div>
              <div className="text-sm text-white/80">credits</div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-12">
          <Link
            to="/jobseeker/credits"
            className="bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
          >
            <div className="flex items-center gap-3 mb-3">
              <ShoppingBag className="w-8 h-8" />
              <h3 className="text-xl font-bold">Buy Credits</h3>
            </div>
            <p className="text-white/90 mb-4">
              Purchase credit packs to unlock premium features
            </p>
            <div className="flex items-center gap-2 text-white font-semibold">
              <span>View Store</span>
              <ArrowRight className="w-5 h-5" />
            </div>
          </Link>

          <Link
            to="/jobseeker/settings"
            className="bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
          >
            <div className="flex items-center gap-3 mb-3">
              <History className="w-8 h-8" />
              <h3 className="text-xl font-bold">Transaction History</h3>
            </div>
            <p className="text-white/90 mb-4">
              View all your credit earnings and spending
            </p>
            <div className="flex items-center gap-2 text-white font-semibold">
              <span>View History</span>
              <ArrowRight className="w-5 h-5" />
            </div>
          </Link>

          <Link
            to="/swipe"
            className="bg-gradient-to-br from-pink-500 to-red-500 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
          >
            <div className="flex items-center gap-3 mb-3">
              <Briefcase className="w-8 h-8" />
              <h3 className="text-xl font-bold">Browse Jobs</h3>
            </div>
            <p className="text-white/90 mb-4">
              Swipe through jobs and earn credits
            </p>
            <div className="flex items-center gap-2 text-white font-semibold">
              <span>Start Swiping</span>
              <ArrowRight className="w-5 h-5" />
            </div>
          </Link>
        </div>

        <div className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Earn Credits</h2>
              <p className="text-gray-600 mt-1">Complete activities to earn free credits</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {earningOpportunities.map((opportunity) => {
              const Icon = opportunity.icon;
              return (
                <div
                  key={opportunity.name}
                  className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-green-300 transition-all hover:shadow-lg"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className={`w-12 h-12 bg-gradient-to-br ${opportunity.color} rounded-xl flex items-center justify-center flex-shrink-0`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-green-600">
                        <Plus className="w-4 h-4" />
                        <span className="text-xl font-bold">{opportunity.cost}</span>
                      </div>
                      <div className="text-xs text-gray-500">credits</div>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {opportunity.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">{opportunity.description}</p>
                  {opportunity.link && (
                    <Link
                      to={opportunity.link}
                      className="w-full py-2 px-4 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition text-sm flex items-center justify-center gap-2"
                    >
                      Start Earning
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Spend Credits</h2>
              <p className="text-gray-600 mt-1">Unlock premium features and services</p>
            </div>
            <Link
              to="/jobseeker/credits"
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg flex items-center gap-2"
            >
              <Coins className="w-5 h-5" />
              Get More Credits
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {spendingOptions.map((option) => {
              const Icon = option.icon;
              const canAfford = balance >= option.cost;

              return (
                <div
                  key={option.name}
                  className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-cyan-300 transition-all hover:shadow-lg"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className={`w-12 h-12 bg-gradient-to-br ${option.color} rounded-xl flex items-center justify-center flex-shrink-0`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-orange-600">
                        <Coins className="w-4 h-4" />
                        <span className="text-xl font-bold">{option.cost}</span>
                      </div>
                      <div className="text-xs text-gray-500">credits</div>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {option.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">{option.description}</p>
                  {option.link ? (
                    <Link
                      to={option.link}
                      className={`w-full py-2 px-4 rounded-lg font-semibold transition text-sm flex items-center justify-center gap-2 ${
                        canAfford
                          ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {canAfford ? 'Use Feature' : 'Need More Credits'}
                    </Link>
                  ) : (
                    <div className="w-full py-2 px-4 bg-gray-100 text-gray-600 rounded-lg font-semibold text-sm text-center">
                      Coming Soon
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                <History className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Recent Activity</h2>
                <p className="text-sm text-gray-600">Your latest credit transactions</p>
              </div>
            </div>

            {loadingTransactions ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
              </div>
            ) : recentTransactions.length === 0 ? (
              <div className="text-center py-12">
                <Coins className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-semibold">No transactions yet</p>
                <p className="text-gray-500 text-sm mt-2">Start earning and spending credits!</p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {recentTransactions.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            transaction.amount > 0
                              ? 'bg-green-100'
                              : 'bg-red-100'
                          }`}
                        >
                          {transaction.amount > 0 ? (
                            <Plus className="w-5 h-5 text-green-600" />
                          ) : (
                            <Coins className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">
                            {transaction.description}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(transaction.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                      <div
                        className={`text-lg font-bold ${
                          transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {transaction.amount > 0 ? '+' : ''}
                        {transaction.amount}
                      </div>
                    </div>
                  ))}
                </div>
                <Link
                  to="/jobseeker/settings"
                  className="mt-6 w-full py-3 px-6 bg-cyan-500 text-white rounded-xl font-bold hover:bg-cyan-600 transition flex items-center justify-center gap-2"
                >
                  View Full History
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </>
            )}
          </div>

          <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="w-10 h-10" />
              <h2 className="text-2xl font-bold">How Credits Work</h2>
            </div>

            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Check className="w-6 h-6 bg-white/20 rounded-full p-1" />
                  <h3 className="text-lg font-bold">Earn Credits Free</h3>
                </div>
                <ul className="space-y-2 text-white/90 text-sm ml-8">
                  <li>• Swipe right on jobs to earn 5 credits</li>
                  <li>• Complete your profile for 10 credits</li>
                  <li>• Upload resume to earn 15 credits</li>
                  <li>• 100 credit welcome bonus</li>
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Coins className="w-6 h-6 bg-white/20 rounded-full p-1" />
                  <h3 className="text-lg font-bold">Spend Wisely</h3>
                </div>
                <ul className="space-y-2 text-white/90 text-sm ml-8">
                  <li>• Build professional resumes with AI</li>
                  <li>• Apply to jobs automatically</li>
                  <li>• Generate custom cover letters</li>
                  <li>• Boost your profile visibility</li>
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <ShoppingBag className="w-6 h-6 bg-white/20 rounded-full p-1" />
                  <h3 className="text-lg font-bold">Need More?</h3>
                </div>
                <p className="text-white/90 text-sm ml-8 mb-4">
                  Purchase credit packs anytime for unlimited access to premium features.
                </p>
                <Link
                  to="/jobseeker/credits"
                  className="ml-8 inline-flex items-center gap-2 px-6 py-3 bg-white text-cyan-600 rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg"
                >
                  <ShoppingBag className="w-5 h-5" />
                  Browse Credit Packs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
